import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, Chip } from "@mui/material";
import { Edit, Close } from "@mui/icons-material";

const SubjectDetails = ({ isOpen, subject, onClose, onEdit, darkMode }) => {
  if (!subject) return null;

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2 },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const modalBg = darkMode ? "bg-gray-800" : "bg-white";
  const textPrimary = darkMode ? "text-white" : "text-gray-800";
  const textSecondary = darkMode ? "text-gray-300" : "text-gray-600";
  const headerBorder = darkMode ? "border-gray-700" : "border-gray-100";

  // Extract all topics from chapters
  const allTopics =
    subject.chapters?.flatMap((chapter) => chapter.topics || []) || [];

  return (
    <AnimatePresence>
      {isOpen && subject && (
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`${modalBg} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-300`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className={`flex items-center justify-between p-6 border-b ${headerBorder}`}
            >
              <div className="flex items-center space-x-3">
                <span className="text-4xl">{subject.icon}</span>
                <div>
                  <h2 className={`text-2xl font-bold ${textPrimary}`}>
                    {subject.subject}
                  </h2>
                  <p className={textSecondary}>
                    Created on{" "}
                    {new Date(subject.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`p-2 ${
                  darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                } rounded-full transition-colors duration-300`}
              >
                <Close className={textPrimary} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Description */}
              <div>
                <h3 className={`text-lg font-semibold ${textPrimary} mb-2`}>
                  Description
                </h3>
                <p className={`${textSecondary} leading-relaxed`}>
                  {subject.description}
                </p>
              </div>

              {/* Popular Badge */}
              <div className="flex items-center justify-between">
                <span className={`text-lg font-semibold ${textPrimary}`}>
                  Status
                </span>
                {subject.isPopular ? (
                  <Chip
                    label="Popular"
                    color="secondary"
                    variant="filled"
                    sx={{
                      backgroundColor: darkMode ? "#EC4899" : "#EC4899",
                      color: "white",
                    }}
                  />
                ) : (
                  <Chip
                    label="Standard"
                    variant="outlined"
                    sx={{
                      borderColor: darkMode ? "#6B7280" : "#D1D5DB",
                      color: darkMode ? "#E5E7EB" : "#374151",
                    }}
                  />
                )}
              </div>

              {/* Exams Section */}
              <div>
                <h3 className={`text-lg font-semibold ${textPrimary} mb-3`}>
                  Related Exams ({subject.exams?.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {subject.exams?.map((exam, index) => (
                    <Chip
                      key={exam}
                      label={exam}
                      color="primary"
                      variant="outlined"
                      sx={{
                        borderColor: darkMode ? "#3B82F6" : "#3B82F6",
                        color: darkMode ? "#93C5FD" : "#1E40AF",
                        backgroundColor: darkMode
                          ? "rgba(59, 130, 246, 0.1)"
                          : "rgba(59, 130, 246, 0.1)",
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Chapters Section */}
              <div>
                <h3 className={`text-lg font-semibold ${textPrimary} mb-3`}>
                  Chapters ({subject.chapters?.length})
                </h3>
                {subject.chapters?.length > 0 ? (
                  <div className="space-y-3">
                    {subject.chapters.map((chapter, index) => (
                      <div
                        key={chapter.id || index}
                        className={`border rounded-lg p-4 ${
                          darkMode
                            ? "border-gray-600 bg-gray-700"
                            : "border-gray-200 bg-gray-50"
                        } transition-colors duration-300`}
                      >
                        <h4 className={`font-semibold ${textPrimary} mb-2`}>
                          {chapter.name || `Chapter ${index + 1}`}
                        </h4>
                        {chapter.topics && chapter.topics.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {chapter.topics
                              .slice(0, 3)
                              .map((topic, topicIndex) => (
                                <span
                                  key={topic}
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    darkMode
                                      ? "bg-green-900 text-green-200"
                                      : "bg-green-100 text-green-700"
                                  }`}
                                >
                                  {topic}
                                </span>
                              ))}
                            {chapter.topics.length > 3 && (
                              <span
                                className={`px-2 py-1 text-xs ${textSecondary}`}
                              >
                                +{chapter.topics.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div
                    className={`border rounded-lg p-4 ${
                      darkMode
                        ? "bg-yellow-900 border-yellow-700"
                        : "bg-yellow-50 border-yellow-200"
                    } transition-colors duration-300`}
                  >
                    <p
                      className={
                        darkMode ? "text-yellow-200" : "text-yellow-800"
                      }
                    >
                      No chapters available yet
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className={`flex justify-end space-x-3 p-6 border-t ${headerBorder}`}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  color: darkMode ? "#E5E7EB" : "#374151",
                  borderColor: darkMode ? "#4B5563" : "#D1D5DB",
                  "&:hover": {
                    borderColor: darkMode ? "#6B7280" : "#9CA3AF",
                    backgroundColor: darkMode
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={onEdit}
                sx={{
                  background: "linear-gradient(135deg, #3B82F6, #6366F1)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #2563EB, #4F46E5)",
                  },
                }}
              >
                Edit Subject
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SubjectDetails;
